import { z } from 'zod';

import type {
  ReportConfigFilterProps,
  ReportConfigFilters,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { getLogger } from 'app-components/utils/core';

const log = getLogger('app-components/flux/wellness-score/wellnessScoreTypes');

export const WELLNESS_SCORE_REDUCER_KEY = 'wellnessScore';

// ================================================================================================
// Request — POST /reports/wellness
// ================================================================================================

/** `BUSINESS_WELLNESS` is accepted by QCS but currently answers 501 Not Implemented. */
export type WellnessType = 'PERSONAL_WELLNESS' | 'BUSINESS_WELLNESS';

/** QCS reads the first configuration of each source and uses its `filter` for that report. */
export type WellnessReportSource = 'NET_WORTH' | 'SPENDING_DASHBOARD';

export type WellnessReportConfiguration = {
  source: WellnessReportSource;
  filter?: ReportConfigFilterProps | ReportConfigFilters | null;
};

// ================================================================================================
// Weights — shared by the request override and every aggregate in the response
// ================================================================================================

export const ControlWeightsSchema = z.object({
  spending: z.number(),
  cashFlow: z.number(),
  creditUtilization: z.number(),
});
export type ControlWeights = z.infer<typeof ControlWeightsSchema>;

export const GrowthWeightsSchema = z.object({
  netWorth: z.number(),
  debt: z.number(),
  creditScore: z.number(),
});
export type GrowthWeights = z.infer<typeof GrowthWeightsSchema>;

export const ResilienceWeightsSchema = z.object({
  cushion: z.number(),
  liquidity: z.number(),
  flexibility: z.number(),
});
export type ResilienceWeights = z.infer<typeof ResilienceWeightsSchema>;

export const TotalWeightsSchema = ControlWeightsSchema.merge(GrowthWeightsSchema).merge(ResilienceWeightsSchema);
export type TotalWeights = z.infer<typeof TotalWeightsSchema>;

/** QCS requires every group and every weight (each ≥ 0) whenever `weights` is sent. */
export type WellnessWeights = {
  control: ControlWeights;
  growth: GrowthWeights;
  resilience: ResilienceWeights;
  total: TotalWeights;
};

export type WellnessScoreRequest = {
  type: WellnessType;
  /** Required by QCS, but may be empty. */
  reportConfigurations: WellnessReportConfiguration[];
  /** Omit to use the QCS default weights. */
  weights?: WellnessWeights;
};

// ================================================================================================
// Response — WellnessScoreResource
// ================================================================================================

/** QCS sends null for anything it could not compute; normalize an omitted field to null as well. */
const nullable = <T extends z.ZodTypeAny>(schema: T) =>
  schema.nullish().transform((value): z.infer<T> | null => value ?? null);

/**
 * Parses one response entry on its own, so a malformed entry degrades to null (unavailable) instead of
 * rejecting the whole response and discarding every entry that did match. Only the entry's path and the
 * validation issues are logged, never the received values, which are the user's financial data.
 */
const lenient = <T extends z.ZodTypeAny>(schema: T) =>
  z.unknown().transform((value, ctx): z.infer<T> | null => {
    if (value === null || value === undefined) return null;
    const result = schema.safeParse(value);
    if (result.success) return result.data;
    log.error('Wellness score response entry does not match the expected shape; treating it as unavailable', {
      path: ctx.path.join('.'),
      issues: result.error.issues.map(({ path, code, message }) => ({ path: path.join('.'), code, message })),
    });
    return null;
  });

export const WellnessTierLevelSchema = z.enum(['EXCELLENT', 'GOOD', 'FAIR', 'NEEDS_WORK']);
export type WellnessTierLevel = z.infer<typeof WellnessTierLevelSchema>;

export const WellnessLiquidityZoneLevelSchema = z.enum(['TOO_TIED_UP', 'GOOD_BALANCE', 'TOO_MUCH_IDLE_CASH']);
export type WellnessLiquidityZoneLevel = z.infer<typeof WellnessLiquidityZoneLevelSchema>;

/** One band of a scale; a null bound is unbounded. */
const makeBandSchema = <L extends z.ZodTypeAny>(levelSchema: L) =>
  z.object({
    level: levelSchema,
    min: nullable(z.number()),
    minInclusive: z.boolean(),
    max: nullable(z.number()),
    maxInclusive: z.boolean(),
  });

/** `basis` names the response field the level was classified from; `levels` is ordered best first. */
const makeScaleSchema = <L extends z.ZodTypeAny>(levelSchema: L) =>
  z.object({
    level: levelSchema,
    basis: z.string(),
    levels: z.array(makeBandSchema(levelSchema)),
  });

export const WellnessTierSchema = makeScaleSchema(WellnessTierLevelSchema);
export type WellnessTier = z.infer<typeof WellnessTierSchema>;
export type WellnessTierBand = WellnessTier['levels'][number];

export const WellnessLiquidityZoneSchema = makeScaleSchema(WellnessLiquidityZoneLevelSchema);
export type WellnessLiquidityZone = z.infer<typeof WellnessLiquidityZoneSchema>;

/** `score` and `tier` are null when none of the aggregate's metrics could be scored. */
const makeAggregateSchema = <W extends z.ZodTypeAny>(weightsSchema: W) =>
  z.object({
    score: nullable(z.number()),
    tier: nullable(WellnessTierSchema),
    weights: weightsSchema,
  });

/** A null aggregate did not match the expected shape (logged); the others are still returned. */
export const WellnessAggregatesSchema = z.object({
  control: lenient(makeAggregateSchema(ControlWeightsSchema)),
  growth: lenient(makeAggregateSchema(GrowthWeightsSchema)),
  resilience: lenient(makeAggregateSchema(ResilienceWeightsSchema)),
  total: lenient(makeAggregateSchema(TotalWeightsSchema)),
});
export type WellnessAggregates = z.infer<typeof WellnessAggregatesSchema>;
export type WellnessAggregateId = keyof WellnessAggregates;
export type WellnessAggregate = NonNullable<WellnessAggregates[WellnessAggregateId]>;

/** Completed months in every history array. */
export const WELLNESS_HISTORY_MONTHS = 6;
/** QCS resolves the wellness month window in this zone, so history months must be labeled in it too. */
export const WELLNESS_SCORE_TIME_ZONE = 'America/Los_Angeles';

const historyValuesSchema = z.array(z.number().nullable());

/** Histories are oldest first and end at the latest completed month; missing older months stay unavailable. */
const completedMonthHistorySchema = historyValuesSchema
  .transform((values): (number | null)[] => {
    const recent = values.slice(-WELLNESS_HISTORY_MONTHS);
    const missing: null[] = Array.from({ length: WELLNESS_HISTORY_MONTHS - recent.length }, () => null);
    return [...missing, ...recent];
  })
  .pipe(historyValuesSchema.length(WELLNESS_HISTORY_MONTHS));

/**
 * Why QCS could not score a metric (its score is null), mirroring QCS `WellnessScoreReason`. The one exception is
 * `NEGATIVE_NET_WORTH_SCALED`, which marks a Net Worth score that was scaled rather than withheld.
 */
export const WELLNESS_SCORE_REASONS = [
  /** No personal accounts to report on, or no credit profile. Won't change until the user adds one. */
  'NO_ACCOUNTS',
  /** A source report, the accounts or the credit report failed to load or came back incomplete. Worth retrying. */
  'DATA_UNAVAILABLE',
  /** Not enough completed months: Net Worth, Spending Rate, Cash Flow, Flexibility, Cushion. */
  'NOT_ENOUGH_HISTORY',
  /** No income in the scored month (Spending Rate, Flexibility), or average income under $100 (Cash Flow). */
  'INSUFFICIENT_INCOME',
  /** Net Worth: the first month is within $1 of zero, so it can't anchor a % change. */
  'FIRST_MONTH_NEAR_ZERO',
  /** Liquidity: net worth is $1 or less, so liquid assets can't be a share of it. */
  'NEGATIVE_NET_WORTH',
  /** Net Worth: the latest month is negative, so the score is scaled to 80% of the curve. Still scored. */
  'NEGATIVE_NET_WORTH_SCALED',
  /** Cushion: no essential spending to cover. */
  'NO_ESSENTIAL_SPENDING',
  /** Debt to Assets: assets are missing or $1 or less. */
  'NO_ASSETS',
  /** Credit Utilization: no counted credit card with a positive total limit. */
  'NO_CREDIT_LIMITS',
  /** Credit Score: the credit report's score isn't a number from 300 to 850. */
  'INVALID_CREDIT_SCORE',
] as const;

export const WellnessScoreReasonSchema = z.enum(WELLNESS_SCORE_REASONS);
export type WellnessScoreReason = z.infer<typeof WellnessScoreReasonSchema>;

export const isWellnessScoreReason = (value: unknown): value is WellnessScoreReason =>
  WellnessScoreReasonSchema.safeParse(value).success;

/** Null on a normally scored metric. A reason this client doesn't know yet degrades to null rather than dropping the metric. */
const reasonSchema = z
  .string()
  .nullish()
  .transform((value): WellnessScoreReason | null => {
    if (value === null || value === undefined) return null;
    if (isWellnessScoreReason(value)) return value;
    log.warn('Unrecognized wellness score reason; treating it as absent', { reason: value });
    return null;
  });

type NullableShape<S extends z.ZodRawShape> = { [K in keyof S]: ReturnType<typeof nullable<S[K]>> };

const toNullableShape = <S extends z.ZodRawShape>(shape: S): NullableShape<S> =>
  Object.fromEntries(Object.entries(shape).map(([key, schema]) => [key, nullable(schema)])) as NullableShape<S>;

/** QCS sends null for a metric it could not score; normalize an omitted score to null as well. */
const hasNoScore = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  (!('score' in value) || value.score === null || value.score === undefined);

/**
 * A scored metric carries every detail; its `reason` is null unless the score was adjusted. An unscored metric
 * (score null) keeps its `reason` and whichever details QCS could still compute, so clients can explain it. The
 * entry is validated against the shape its score selects, so a malformed entry logs that shape's issues.
 */
const makeMetricSchema = <S extends z.ZodRawShape>(details: S) => {
  const scored = z.object({ score: z.number(), tier: WellnessTierSchema, reason: reasonSchema, ...details });
  const unscored = z.object({
    score: nullable(z.null()),
    tier: nullable(WellnessTierSchema),
    reason: reasonSchema,
    ...toNullableShape(details),
  });
  return z.unknown().transform((value, ctx): z.infer<typeof scored> | z.infer<typeof unscored> => {
    const result = (hasNoScore(value) ? unscored : scored).safeParse(value);
    if (result.success) return result.data;
    for (const issue of result.error.issues) ctx.addIssue(issue);
    return z.NEVER;
  });
};

/** Narrows a metric to the variant QCS scored, which carries every detail. */
export type ScoredWellnessMetric<M extends { score: number | null }> = Extract<M, { score: number }>;

export const isScoredWellnessMetric = <M extends { score: number | null }>(
  metric: M | null | undefined,
): metric is ScoredWellnessMetric<M> => typeof metric?.score === 'number';

export const SpendingMetricSchema = makeMetricSchema({
  ratePercent: z.number(),
  income: z.number(),
  spent: z.number(),
  /** Last six completed months, oldest first; null when that month has no positive income. */
  monthlyRatePercent: completedMonthHistorySchema,
});
export type SpendingMetric = z.infer<typeof SpendingMetricSchema>;

export const CashFlowMetricSchema = makeMetricSchema({
  volatility: z.number(),
  /** Unavailable when averageNet is zero; the volatility score is still valid. */
  deviationPercent: nullable(z.number()),
  monthlyNet: completedMonthHistorySchema,
  averageNet: z.number(),
});
export type CashFlowMetric = z.infer<typeof CashFlowMetricSchema>;

export const CreditUtilizationMetricSchema = makeMetricSchema({
  utilizationPercent: z.number(),
});
export type CreditUtilizationMetric = z.infer<typeof CreditUtilizationMetricSchema>;

export const NetWorthMetricSchema = makeMetricSchema({
  changePercent: z.number(),
  monthEndNetWorth: completedMonthHistorySchema,
});
export type NetWorthMetric = z.infer<typeof NetWorthMetricSchema>;

/** The trend fields are null when QCS scores position only (fewer than six valid months). */
export const DebtMetricSchema = makeMetricSchema({
  debtToAssetPercent: z.number(),
  changePercent: nullable(z.number()),
  priorDebtToAssetPercent: nullable(z.number()),
});
export type DebtMetric = z.infer<typeof DebtMetricSchema>;

export const CreditScoreMetricSchema = makeMetricSchema({
  /** VantageScore 3.0, 300–850. */
  rawScore: z.number(),
});
export type CreditScoreMetric = z.infer<typeof CreditScoreMetricSchema>;

export const CushionMetricSchema = makeMetricSchema({
  /** Unrounded so clients can truncate years correctly. */
  months: z.number(),
  averageMonthlyEssentials: z.number(),
});
export type CushionMetric = z.infer<typeof CushionMetricSchema>;

export const LiquidityMetricSchema = makeMetricSchema({
  liquidPercent: z.number(),
  zone: WellnessLiquidityZoneSchema,
});
export type LiquidityMetric = z.infer<typeof LiquidityMetricSchema>;

export const FlexibilityMetricSchema = makeMetricSchema({
  fixedCostPercent: z.number(),
});
export type FlexibilityMetric = z.infer<typeof FlexibilityMetricSchema>;

/**
 * A null metric is missing from the response, or its entry did not match the expected shape (logged) and is
 * treated the same way. An unscored metric (score null) is kept with its reason. Either way it is left out of
 * every aggregate, and the other metrics are still returned.
 */
export const WellnessMetricsSchema = z.object({
  spending: lenient(SpendingMetricSchema),
  cashFlow: lenient(CashFlowMetricSchema),
  creditUtilization: lenient(CreditUtilizationMetricSchema),
  netWorth: lenient(NetWorthMetricSchema),
  debt: lenient(DebtMetricSchema),
  creditScore: lenient(CreditScoreMetricSchema),
  cushion: lenient(CushionMetricSchema),
  liquidity: lenient(LiquidityMetricSchema),
  flexibility: lenient(FlexibilityMetricSchema),
});
export type WellnessMetrics = z.infer<typeof WellnessMetricsSchema>;
export type WellnessMetricId = keyof WellnessMetrics;

export const WellnessScoreSchema = z.object({
  aggregates: WellnessAggregatesSchema,
  metrics: WellnessMetricsSchema,
});
export type WellnessScore = z.infer<typeof WellnessScoreSchema>;

// ================================================================================================
// Summary — the displayed score and tier of every aggregate and metric
// ================================================================================================

/** Both null when the entry is unavailable. */
export type WellnessScoreValue = {
  score: number | null;
  tier: WellnessTierLevel | null;
};

export type WellnessGroupAggregateId = Exclude<WellnessAggregateId, 'total'>;

export type WellnessScoreSummary = {
  total: WellnessScoreValue;
  groups: Record<WellnessGroupAggregateId, WellnessScoreValue>;
  metrics: Record<WellnessMetricId, WellnessScoreValue>;
};

/** Supplied by the thunk so the reducer stays pure. */
export type WellnessScoreFulfilledMeta = {
  /** ISO timestamp at which the client received a valid response. */
  fetchedAt: string;
};

// ================================================================================================
// Store
// ================================================================================================

/** Serializable failures exposed by the thunk and selectors; raw responses are not stored. */
export type WellnessScoreRequestError =
  | { kind: 'http'; message: string; status: number; qcsCode: string | null }
  | { kind: 'transport'; message: string; code: string | null }
  | {
      kind: 'schema';
      message: string;
      issues: { path: (string | number)[]; code: string; message: string }[];
    }
  | { kind: 'unknown'; message: string };

export type WellnessScoreStoreState = {
  score: WellnessScore | null;
  isLoading: boolean;
  error: WellnessScoreRequestError | null;
  /** `fetchedAt` of the last successful response. */
  lastUpdatedAt: string | null;
  /** The in-flight request; responses from superseded requests are ignored. */
  requestId: string | null;
};

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [WELLNESS_SCORE_REDUCER_KEY]: WellnessScoreStoreState;
  }
}
